import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  ChildrenProp,
  ClassNameProp,
  ClosableComponentProps
} from '../common-types';
import Draggable, { DraggableProps } from 'react-draggable';
import { Tool } from './Tool';
import { Icon, ValidIconName } from './Icon';
import { intl, noPropagationClickHandler } from '../../lib';
import { isEmptyString } from '../../util';
import { ResizableBox, ResizableBoxProps } from 'react-resizable';
import FocusTrap from 'focus-trap-react';
import { VBox } from '../layout';

/**
 * Complex type for the `resizable` property of {@link ModalProps}, to
 * be able to export the correct type with all possible 'overloads' to outside (wrapper) components.
 */
export type ModalResizable =
  | boolean
  | ResizableBoxProps
  | (Omit<ResizableBoxProps, 'width' | 'height'> & {
      width?: undefined;
      height?: undefined;
    });

/**
 * Props for a {@link Modal} component.
 * @author agd
 */
export interface ModalProps
  extends ClosableComponentProps,
    ClassNameProp,
    Required<ChildrenProp> {
  /**
   * Draggable options for the modal.
   */
  draggable?: boolean | Partial<DraggableProps>;
  /**
   * If the modal overlay is shown or not.
   */
  showOverlay?: boolean;
  /**
   * Handler for when the overlay is clicked.
   */
  onOverlayClicked?: React.MouseEventHandler;
  /**
   * Title of the modal.
   */
  title?: React.ReactNode;
  /**
   * Buttons/Tools to put on the bottom right.
   *
   * The primary action should come first because it will be aligned to the right (flex-direction: row-reversed).
   */
  actions?: JSX.Element;
  /**
   * Optional icon or element to show next to the title.
   */
  icon?: ValidIconName | React.ReactElement;
  /**
   * If the modal can be expanded (maximized).
   */
  expandable?: boolean;
  /**
   * The initial expanded state to set. This defaults to false.
   */
  initialExpanded?: boolean;
  /**
   * Resizable settings. This can either be a boolean or {@ linkResizableBoxProps}.
   */
  resizable?: ModalResizable;
  /**
   * List of elements which should be clickable outside modal even if a modal is opened or true if all should be clickable.
   */
  outsideClickable?: string[] | boolean;
  /**
   * Whether to apply initial offset from drag bound container (suits in case container is not part of parent tree).
   */
  centerByDragBounds?: boolean;
}

/**
 * A Modal component for wrapping components with to be able to display any component in a modal format.
 *
 * @param props a {@link ModalProps} object
 * @return the created {@link JSX.Element}
 * @constructor
 * @author agd
 */
export function Modal({
  children,
  draggable = true,
  showOverlay = true,
  onOverlayClicked,
  title,
  className = '',
  closable = false,
  actions,
  icon,
  expandable = false,
  initialExpanded = false,
  resizable,
  outsideClickable = [],
  centerByDragBounds
}: ModalProps): JSX.Element {
  // ref to the modal element
  const modalRef = useRef<HTMLDivElement>(null);
  // track modal dimensions, to be able to center it in the viewport
  const [modalDimensions, setModalDimensions] = useState({
    width: 0,
    height: 0
  });
  const [rendered, setRendered] = useState(false);
  const [resizing, setResizing] = useState(false);
  useLayoutEffect(() => {
    if (modalRef.current) {
      setModalDimensions({
        width: modalRef.current.offsetWidth,
        height: modalRef.current.offsetHeight
      });
    }

    setRendered(true);
  }, []);
  // track expanded state
  const [expanded, setExpanded] = useState(initialExpanded);
  const toggleExpanded = useCallback(() => setExpanded(!expanded), [expanded]);

  // resize handlers, to track if the component is currently being resized
  const onResizeStart = useCallback(
    (e, data) => {
      setResizing(true);
      if (typeof resizable === 'object') resizable?.onResizeStart?.(e, data);
    },
    [resizable]
  );
  const onResizeStop = useCallback(
    (e, data) => {
      setResizing(false);
      if (typeof resizable === 'object') resizable?.onResizeStop?.(e, data);
    },
    [resizable]
  );

  // determine properties passed to the Draggable component if the Modal is draggable
  let draggableProps: Partial<DraggableProps> = {};
  if (draggable && typeof draggable !== 'boolean') {
    draggableProps = { ...draggable };
  }

  // the overlay element
  let overlayElement = null;
  if (showOverlay) {
    overlayElement = (
      <div className="ehs-modal-overlay" onClick={onOverlayClicked} />
    );
  }

  // modal title element
  let titleElement = null;
  if (title || closable || icon) {
    let titleTextClass = 'title-text';
    let iconClass = 'modal-icon';
    // if the Modal is draggable but no handle was specified, then set the title to the handle if it exists
    if (
      title &&
      draggable &&
      (typeof draggable === 'boolean' || !draggable.handle)
    ) {
      titleTextClass += ' drag-handle';
      draggableProps.handle = '.drag-handle';
      iconClass += ' drag-handle';
    }
    let iconNode = React.isValidElement(icon) ? icon : undefined;
    if (typeof icon !== 'undefined') {
      if (typeof icon === 'string') {
        iconNode = (
          <Icon aria-label={undefined} iconName={icon} className={iconClass} />
        );
      }
    }
    titleElement = (
      <div
        className="modal-title"
        onDoubleClick={
          expandable ? noPropagationClickHandler(toggleExpanded) : undefined
        }
      >
        {iconNode}
        <h2 className={titleTextClass}>{title}</h2>
        {expandable && (
          <Tool
            icon={expanded ? 'exit-fullscreen' : 'fullscreen'}
            onClick={toggleExpanded}
            tooltip={
              expanded
                ? intl('view.grid.collapse.tooltip')
                : intl('view.grid.expand.tooltip')
            }
          />
        )}
        {closable && (
          <Tool
            icon="shui-cancel"
            onClick={closable.onClose}
            tooltip={intl('view.button.close')}
          />
        )}
      </div>
    );
  }
  let actionElement = null;
  if (actions) {
    actionElement = <div className="modal-actions">{actions}</div>;
  }

  const classes = ['ehs-modal'];
  if (actions) {
    classes.push('has-actions');
  }
  if (expanded) {
    classes.push('expanded');
  }
  if (resizing) {
    classes.push('resizing');
  }
  if (!isEmptyString(className)) {
    classes.push(className);
  }

  const modalElementClassName = classes.join(' ').trim();
  const modalElementChildren = (
    <FocusTrap
      focusTrapOptions={{
        escapeDeactivates: false,
        allowOutsideClick: (e: MouseEvent | TouchEvent) => {
          if (typeof outsideClickable === 'boolean') {
            return outsideClickable;
          }
          const clickedElement = e.target as HTMLElement;
          return (
            outsideClickable.includes(clickedElement.id) ||
            outsideClickable.some(
              elementIdentifier =>
                clickedElement.classList.contains(elementIdentifier) ||
                clickedElement.closest(`.${elementIdentifier}`) ||
                clickedElement.closest(`#${elementIdentifier}`)
              // The condition controls if there is a match between configured identifiers and currently clicked
              // element's identifier. In case of class we need check the whole class list.There is possibility
              // the clicked element is actually nested child of element which user configured to be clickable.
              // For this case we have two last parts of the condition where also parents are checked.
            )
          );
        }
      }}
    >
      <VBox>
        {titleElement}
        <div className="modal-content">{children}</div>
        {actionElement}
      </VBox>
    </FocusTrap>
  );

  let modalElement;
  if (
    resizable &&
    // the 'rendered' flag is to capture the size of the "normal" auto-sizing modal element, to be able to automatically
    // set the width and height, without needing to pass them
    (rendered ||
      // we don't care about the 'rendered' flag if we have pre-defined width and height
      (typeof resizable === 'object' &&
        typeof resizable.width === 'number' &&
        typeof resizable.height === 'number'))
  ) {
    // get box width and height
    let resizableBoxSize = {
      width: modalDimensions.width,
      height: modalDimensions.height
    };
    if (typeof resizable === 'object') {
      if (
        typeof resizable.width === 'number' &&
        typeof resizable.height === 'number'
      ) {
        resizableBoxSize = {
          width: resizable.width,
          height: resizable.height
        };
      }

      // overwrite width and height with min/max values if they are below/above the min/max threshold
      // this is necessary specifically for the case when width and height are set **automatically**
      const minWidth = resizable?.minConstraints?.[0];
      const minHeight = resizable?.minConstraints?.[1];
      const maxWidth = resizable?.maxConstraints?.[0];
      const maxHeight = resizable?.maxConstraints?.[1];

      if (minWidth && resizableBoxSize.width < minWidth)
        resizableBoxSize.width = minWidth;
      if (minHeight && resizableBoxSize.height < minHeight)
        resizableBoxSize.height = minHeight;
      if (maxWidth && resizableBoxSize.width > maxWidth)
        resizableBoxSize.width = maxWidth;
      if (maxHeight && resizableBoxSize.height > maxHeight)
        resizableBoxSize.height = maxHeight;
    }

    modalElement = (
      <ResizableBox
        {...(typeof resizable === 'object' && resizable)}
        {...resizableBoxSize}
        className={modalElementClassName}
        onResizeStart={onResizeStart}
        onResizeStop={onResizeStop}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        style={getCenteredStyle(
          resizableBoxSize,
          centerByDragBounds && typeof draggableProps.bounds === 'string'
            ? draggableProps.bounds
            : 'body'
        )}
      >
        <div className="modal-resizable-area" ref={modalRef}>
          {modalElementChildren}
        </div>
      </ResizableBox>
    );
  } else {
    modalElement = (
      <div
        className={modalElementClassName}
        ref={modalRef}
        style={getCenteredStyle(
          modalDimensions,
          centerByDragBounds && typeof draggableProps.bounds === 'string'
            ? draggableProps.bounds
            : 'body'
        )}
      >
        {modalElementChildren}
      </div>
    );
  }

  if (draggable) {
    modalElement = <Draggable {...draggableProps}>{modalElement}</Draggable>;
  }

  return (
    <>
      {overlayElement}
      {modalElement}
    </>
  );
}

function getCenteredStyle(
  { height, width }: { height: number; width: number },
  bounds = 'body'
): Pick<CSSStyleDeclaration, 'left' | 'top'> {
  let offsetX = width / 2;
  let offsetY = height / 2;

  if (bounds !== 'body') {
    const { x = 0, y = 0 } =
      (document.querySelector(
        bounds
      ) as HTMLElement)?.getBoundingClientRect() ?? {};
    offsetX += x / 2;
    offsetY += y / 2;
  }

  return {
    left: `calc(50% - ${offsetX}px)`,
    top: `calc(50% - ${offsetY}px)`
  };
}
